/**
 * FP3-119B — Human-friendly "updated X ago" formatter for the small
 * refresh timestamp shown next to the Refresh button on operational
 * admin pages.
 */
export function formatUpdatedAgo(timestampMs) {
  if (!timestampMs) return "";
  const seconds = Math.max(0, Math.floor((Date.now() - timestampMs) / 1000));
  if (seconds < 5) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

/**
 * A tiny shared component used on operational admin pages to show
 * cache freshness + a manual Refresh action.
 */
import { ArrowsClockwise } from "@phosphor-icons/react";
import { useState, useEffect } from "react";

export function RefreshChip({
  dataUpdatedAt,
  isFetching,
  onRefresh,
  testid = "refresh-chip",
}) {
  // Force a re-render every 30s so the "Updated 2 min ago" label
  // stays roughly accurate without polling the server.
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);
  return (
    <div
      className="inline-flex items-center gap-1.5 text-[11px] text-[#6B7280]"
      data-testid={testid}
    >
      {dataUpdatedAt ? (
        <span data-testid={`${testid}-updated-at`}>
          Updated {formatUpdatedAgo(dataUpdatedAt)}
        </span>
      ) : null}
      <button
        type="button"
        onClick={onRefresh}
        disabled={isFetching}
        data-testid={`${testid}-btn`}
        className={`inline-flex items-center gap-1 rounded-full border border-[#E5E3DB] bg-white px-2 py-0.5 text-[10.5px] font-medium text-[#3F4756] hover:bg-[#F5F3EE] transition-colors ${
          isFetching ? "opacity-60 cursor-wait" : ""
        }`}
        title={isFetching ? "Refreshing…" : "Refresh"}
      >
        <ArrowsClockwise
          size={11}
          weight="bold"
          className={isFetching ? "animate-spin" : ""}
        />
        {isFetching ? "Refreshing…" : "Refresh"}
      </button>
    </div>
  );
}
